import React, { useCallback } from "react";

import { useBusyBlockerContext } from "../../contexts/busy-blocker-context";
import { useDashboardContext } from "../../contexts/dashboard-context";

import { useAppContext } from "../../hooks/useAppContext";
import { useErrorHandling } from "../../hooks/useErrorHandling";
import { useLabels } from "../../hooks/useLabels";
import { useNavigateToUrl } from "../../hooks/useNavigate";
import { useProductCategory } from "../../hooks/useProductCategory";
import { useProductCode } from "../../hooks/useProductCode";
import { useSnackbar } from "../../hooks/useSnackbar";

import { showInfoDialogAsync } from "../dialogs/info-dialog";
import { showProductWarningsDialogAsync } from "../dialogs/product-warnings-dialog";
import { showRestartProductDialogAsync } from "../dialogs/restart-product-dialog";
import { showSaveAndConfirmDialogAsync } from "../dialogs/save-dialog";

import { Version } from "../version";

import { useCdnUrl } from "../../hooks/useCdnUrl";
import { useProductErrorsAndWarnings } from "../../hooks/useProductErrorsAndWarnings";
import { showLeaveDialogAsync } from "../dialogs/leave-dialog";
import { FloatingMenuItem } from "./floating-menu-item";
import { dynamicLabels, labels } from "./labels";

interface IFloatingMenuContentProps {
	onClose: () => void;
}

export function FloatingMenuContent({ onClose }: IFloatingMenuContentProps) {
	const {
		context: { account },
		editor,
		googleAnalytics,
	} = useAppContext();

	const { addSnackbar } = useSnackbar();
	const { busyBlocker } = useBusyBlockerContext();
	const { createErrorHandler } = useErrorHandling();

	const productCode = useProductCode();
	const productCategory = useProductCategory();
	const dashboard = useDashboardContext();

	const { hasProductErrorsOrWarnings, productErrorsAndWarnings } = useProductErrorsAndWarnings();

	const infoMessageProductCategoryLabel =
		dynamicLabels.infoMessageProductCategory(productCategory);
	const infoMessageProductLabel = dynamicLabels.infoMessageProduct(productCode);

	const [
		[
			save,
			saveNotification,
			restartProduct,
			info,
			infoMessageProduct,
			infoMessageProductCategory,
			infoContent,
			savedProducts,
			productWarnings,
		],
		areLabelsLoading,
	] = useLabels([
		labels.save,
		labels.saveNotification,
		labels.restartProduct,
		labels.info,
		infoMessageProductLabel,
		infoMessageProductCategoryLabel,
		labels.infoContent,
		labels.savedProducts,
		labels.productWarnings,
	]);

	const resetOptions = { refill: false };
	const canResetDefinition = dashboard.canResetDefinition(resetOptions);

	const onSaveClick = useCallback(() => {
		onClose();

		googleAnalytics.trackEventToGa({ trackingAction: "SaveProject" });

		const saveAsync = () =>
			showSaveAndConfirmDialogAsync({
				editor,
				busyBlocker,
				getIsSignedIn: () => !!(account?.type === eXpress.core.AccountType.Full),
			});

		saveAsync()
			.then((data) => {
				if (data && saveNotification) {
					addSnackbar({
						id: "save",
						text: saveNotification,
					});
				}
			})
			.catch(createErrorHandler());
	}, [
		onClose,
		googleAnalytics,
		account,
		createErrorHandler,
		busyBlocker,
		editor,
		addSnackbar,
		saveNotification,
	]);

	const onRestartClick = useCallback(() => {
		onClose();

		googleAnalytics.trackEventToGa({
			trackingAction: "RestartProjectClicked",
		});

		const restartAsync = () =>
			showRestartProductDialogAsync({ busyBlocker, dashboard, editor });

		restartAsync().catch(createErrorHandler());
	}, [dashboard, busyBlocker, onClose, createErrorHandler, editor, googleAnalytics]);

	function onInfoClick() {
		onClose();

		// infoMessage:First check for content in infoMessageProduct, then goto infoMessageProductCategory as fallback
		const infoMessage = infoMessageProduct || infoMessageProductCategory;
		// Remove the the link to a help dialog (this HTML code is the value of 'infoContent')
		// Remove leading breaks
		const infoMessageFiltered = infoMessage
			?.replace(infoContent ?? "", "")
			.replace(/<br\/>$/, "")
			.trim();

		if (!infoMessageFiltered) {
			return;
		}

		showInfoDialogAsync({
			messageHtml: infoMessageFiltered,
		}).catch(createErrorHandler());
	}
	const navigateToUrl = useNavigateToUrl();
	const {
		context: { mySavedProductsUrl },
	} = useAppContext();

	const [cdnUrl, setCdnUrl] = useCdnUrl();

	function onMyProductsClick() {
		onClose();

		if (!editor.isDirty()) {
			navigateToUrl(mySavedProductsUrl);
		} else {
			showLeaveDialogAsync()
				.then((shouldLeave) =>
					shouldLeave
						? showSaveAndConfirmDialogAsync({
								isLeavingPage: true,
								editor,
								busyBlocker,
								getIsSignedIn: () =>
									!!(account?.type === eXpress.core.AccountType.Full),
						  }).then((result) => result !== undefined)
						: false,
				)
				.then((shouldLeave) => {
					if (shouldLeave) navigateToUrl(mySavedProductsUrl);
				})
				.catch(createErrorHandler());
		}
	}

	function onProductWarningsClicked() {
		onClose();
		if (productErrorsAndWarnings?.length) {
			return showProductWarningsDialogAsync(productErrorsAndWarnings).catch(
				createErrorHandler(),
			);
		}
		return Promise.resolve(true);
	}

	if (areLabelsLoading) return null;

	return (
		<div className="floating-menu__content">
			<FloatingMenuItem text={save} iconName="save" onClick={onSaveClick} />

			<FloatingMenuItem
				text={restartProduct}
				iconName="restart"
				onClick={onRestartClick}
				disabled={!canResetDefinition}
			/>

			{(infoMessageProduct || infoMessageProductCategory) && (
				<FloatingMenuItem text={info} iconName="info" onClick={onInfoClick} />
			)}

			{mySavedProductsUrl && (
				<FloatingMenuItem
					text={savedProducts}
					iconName="shopping-bag-gift"
					onClick={onMyProductsClick}
				/>
			)}

			{hasProductErrorsOrWarnings && (
				<FloatingMenuItem
					text={
						productWarnings &&
						eXpress.utils.formatString(
							productWarnings,
							productErrorsAndWarnings.length.toString(),
						)
					}
					iconName="low-res"
					onClick={() => {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						onProductWarningsClicked();
					}}
					badge
					isError={productErrorsAndWarnings.some((err) => err.isError)}
				/>
			)}

			{cdnUrl && (
				<FloatingMenuItem
					text="Reset version"
					iconName="offline"
					onClick={() => {
						setCdnUrl(undefined);
						onClose();
					}}
					isError={productErrorsAndWarnings.some((err) => err.isError)}
				/>
			)}

			<Version />
		</div>
	);
}
